import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useRef,
} from "react";
import { useParams } from "react-router-dom";
import { useBluelagoon } from "@bluelagoon";

const TableDataContext = createContext(null);

// Module-level cache (survives re-renders, not page refresh)
const tableDataCache = new Map();
const STALE_MS = 30_000; // 30 seconds

function buildCacheKey(tableId, page, search, filters, colFilters) {
    return `${tableId}::${page || 1}::${search || ""}::${JSON.stringify(
        filters || []
    )}::${JSON.stringify(colFilters || {})}`;
}

const EMPTY_FILTERS = {
    searchQuery: "",
    searchDraft: "",
    filterRules: [],
    columnFilters: {},
    currentPage: 1,
};

export function TableDataProvider({ children }) {
    const { databaseId, tableId } = useParams();
    const [databaseInfo, setDatabaseInfo] = useState(null);
    const [tablesList, setTablesList] = useState([]);
    const [isLoadingDatabase, setIsLoadingDatabase] = useState(false);
    const [filters, setFilters] = useState(() => ({ ...EMPTY_FILTERS, tableId }));
    const tableIdRef = useRef(tableId);
    tableIdRef.current = tableId;
    const filterCacheRef = useRef(new Map());
    const { call: getDatabasesCall } = useBluelagoon({
        method: "getDatabases",
    });
    const { call: getRecordsCall } = useBluelagoon({
        method: "getTableRecords",
    });
    const fetchedDbRef = useRef(null);

    // Save current filters and restore (or reset) when tableId changes
    if (filters.tableId !== tableId) {
        // Cache outgoing table's filters
        const { tableId: oldId, ...oldValues } = filters;
        if (oldId) filterCacheRef.current.set(oldId, oldValues);
        // Restore cached filters for new table, or start fresh
        const cached = filterCacheRef.current.get(tableId);
        setFilters(cached ? { ...cached, tableId } : { ...EMPTY_FILTERS, tableId });
    }

    // Derive active filters — always return defaults when state is stale
    const activeFilters = filters.tableId === tableId ? filters : EMPTY_FILTERS;

    // Guard helper: stale functional updaters become no-ops
    const guardedSet = useCallback((updater) => {
        setFilters((s) => {
            if (s.tableId !== tableIdRef.current) return s;
            return updater(s);
        });
    }, []);

    const setSearchQuery = useCallback((v) => {
        guardedSet((s) => ({ ...s, searchQuery: v }));
    }, [guardedSet]);
    const setSearchDraft = useCallback((v) => {
        guardedSet((s) => ({ ...s, searchDraft: v }));
    }, [guardedSet]);
    const setFilterRules = useCallback((v) => {
        guardedSet((s) => ({
            ...s,
            filterRules: typeof v === "function" ? v(s.filterRules) : v,
        }));
    }, [guardedSet]);
    const setColumnFilters = useCallback((v) => {
        guardedSet((s) => ({
            ...s,
            columnFilters: typeof v === "function" ? v(s.columnFilters) : v,
        }));
    }, [guardedSet]);
    const setCurrentPage = useCallback((v) => {
        guardedSet((s) => ({ ...s, currentPage: v }));
    }, [guardedSet]);

    const resetFilters = useCallback(() => {
        filterCacheRef.current.delete(tableIdRef.current);
        setFilters({ ...EMPTY_FILTERS, tableId: tableIdRef.current });
    }, []);

    // Fetch database info once per databaseId
    const fetchDatabaseInfo = useCallback(async () => {
        if (fetchedDbRef.current === databaseId) return;
        fetchedDbRef.current = databaseId;
        setIsLoadingDatabase(true);
        try {
            const res = await getDatabasesCall();
            const db = res?.data?.databases?.find((d) => d.id === databaseId);
            if (db) {
                setDatabaseInfo({
                    id: db.id,
                    name: db.name,
                    description: db.description,
                });
                setTablesList(db.tables || []);
            }
        } finally {
            setIsLoadingDatabase(false);
        }
    }, [databaseId, getDatabasesCall]);

    React.useEffect(() => {
        if (databaseId) fetchDatabaseInfo();
    }, [databaseId, fetchDatabaseInfo]);

    const getTableData = useCallback(
        (tableId, page, search, filters, colFilters) => {
            const key = buildCacheKey(
                tableId,
                page,
                search,
                filters,
                colFilters
            );
            const entry = tableDataCache.get(key);
            return entry?.data ?? null;
        },
        []
    );

    const fetchTableData = useCallback(
        async (tableId, page, search, filters, colFilters) => {
            const key = buildCacheKey(
                tableId,
                page,
                search,
                filters,
                colFilters
            );
            const res = await getRecordsCall({
                tableId,
                page,
                pageSize: 25,
                searchQuery: search,
                filterRules: filters,
                columnFilters: colFilters,
            });
            if (res?.data) {
                tableDataCache.set(key, {
                    data: res.data,
                    fetchedAt: Date.now(),
                });
            }
            return res;
        },
        [getRecordsCall]
    );

    const invalidateTable = useCallback((tableId) => {
        // Delete all cache entries for this table
        for (const key of tableDataCache.keys()) {
            if (key.startsWith(`${tableId}::`)) {
                tableDataCache.delete(key);
            }
        }
    }, []);

    const isStale = useCallback(
        (tableId, page, search, filters, colFilters) => {
            const key = buildCacheKey(
                tableId,
                page,
                search,
                filters,
                colFilters
            );
            const entry = tableDataCache.get(key);
            if (!entry) return true;
            return Date.now() - entry.fetchedAt > STALE_MS;
        },
        []
    );

    const refreshDatabaseInfo = useCallback(async () => {
        fetchedDbRef.current = null;
        await fetchDatabaseInfo();
    }, [fetchDatabaseInfo]);

    const value = React.useMemo(
        () => ({
            databaseInfo,
            tablesList,
            isLoadingDatabase,
            getTableData,
            fetchTableData,
            invalidateTable,
            isStale,
            refreshDatabaseInfo,
            searchQuery: activeFilters.searchQuery,
            searchDraft: activeFilters.searchDraft,
            filterRules: activeFilters.filterRules,
            columnFilters: activeFilters.columnFilters,
            currentPage: activeFilters.currentPage,
            setSearchQuery,
            setSearchDraft,
            setFilterRules,
            setColumnFilters,
            setCurrentPage,
            resetFilters,
        }),
        [
            databaseInfo,
            tablesList,
            isLoadingDatabase,
            getTableData,
            fetchTableData,
            invalidateTable,
            isStale,
            refreshDatabaseInfo,
            activeFilters,
            setSearchQuery,
            setSearchDraft,
            setFilterRules,
            setColumnFilters,
            setCurrentPage,
            resetFilters,
        ]
    );

    return (
        <TableDataContext.Provider value={value}>
            {children}
        </TableDataContext.Provider>
    );
}

export function useTableData() {
    const ctx = useContext(TableDataContext);
    if (!ctx)
        throw new Error("useTableData must be used within TableDataProvider");
    return ctx;
}
